// React Imports
import ReactPlayer from 'react-player';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

// MUI Components and Hooks Imports
import { Stack } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { Grid, Tooltip, Typography, Avatar, Box, Accordion, AccordionSummary, AccordionDetails, Button } from '@mui/material';

// Icon sImports
import {
  Cloud,
  Music,
  Document,
  ArrowLeft,
  TaskSquare,
  ArrowRight,
  VideoCircle,
  DocumentText,
  DocumentText1,
  TableDocument,
  PresentionChart,
} from 'iconsax-react';

// Component and Functions Imports
import MainCard from 'components/MainCard';
import { modifyUrlFromIndex } from 'utils/helper';
import AnimateButton from 'components/@extended/AnimateButton';

import { useLocation } from 'react-router';
import HeaderOption from './HeaderOption';
import DocumentViewer from './DocumentViewer';


const SuperAdminCourseView = () => {

  const theme = useTheme();

  const location = useLocation()

  const vimeoRef: any = useRef(null);
  const videoPlayer: any = useRef(null);
  const vdoCipherRef: any = useRef(null);

  const [dynamicUrl, setDynamicUrl] = useState('');
  const [isPlaying, setIsPlaying] = useState(true);
  const [dragging, setDragging] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [vimeoPreview, setVimeoPreview] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [vdoCipherPreview, setVdoCipherPreview] = useState(false);
  const [toggleSection, setToggleSection] = useState(true);
  const [expandSectionId, setExpandSectionId] = useState<string>("");
  const [currentContentData, setCurrentContentData] = useState<any>({});
  const [totalCourseDuration, setTotalCourseDuration] = useState<any>("");


  const setting = useSelector((state: any) => state.organization)?.logo?.[0];
  // const expandSectionId = useSelector((state: any) => state.whiteboard)?.expandSectionId;
  // const currentContentData = useSelector((state: any) => state.whiteboard)?.currentContentData;

  const courseData = location.state.courseData
  const section = courseData?.section || [];

  const handleSideToggles = () => {
    setToggleSection((prev) => !prev)
  }

  const convertMinutesToHours = (time: string) => {
    let totalSeconds = 0;
    if (time.includes(":")) {
      // Case: Input is in MM:SS format
      const [minutes, seconds] = time.split(":").map(Number);
      totalSeconds = minutes * 60 + seconds;
    } else {
      // Case: Input is in minutes format
      totalSeconds = Number(time) * 60; // Convert minutes to seconds
    }
  
    const totalMinutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
  
    if (totalMinutes === 0) {
      return `${remainingSeconds} sec`;
    }
  
    const hours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;
  
    if (hours === 0) {
      return `${remainingMinutes} min ${remainingSeconds ? remainingSeconds + " sec" : ""}`.trim();
    }
  
    return `${hours} h ${remainingMinutes} min ${remainingSeconds ? remainingSeconds + " sec" : ""}`.trim();
  };

  const handleChange = (id: string, value?: string) => {
    if (value === '') setExpandSectionId('');
    else setExpandSectionId(id);
  };

  const goToNextContent = () => {
    const currentSection = section.find((section: any) => section._id === expandSectionId);
    if (!currentSection) return;

    const currentContentIndex = currentSection.contentId.findIndex(
      (content: any) => content._id === currentContentData?._id
    );

    if (currentContentIndex === -1) return;

    if (currentContentIndex < currentSection.contentId.length - 1) {
      // Move to the next content in the same section
      setCurrentContentData(currentSection.contentId[currentContentIndex + 1]);
    } else {
      // Move to the first content of the next section
      const nextSectionIndex = section.findIndex(
        (section: any) => section._id === expandSectionId
      ) + 1;

      if (nextSectionIndex < section.length) {
        const nextSection = section[nextSectionIndex];
        setExpandSectionId(nextSection._id);
        setCurrentContentData(nextSection.contentId[0]);
      }
    }
  };

  const goToPreviousContent = () => {
    const currentSection = section.find((section: any) => section._id === expandSectionId);
    if (!currentSection) return;

    const currentContentIndex = currentSection.contentId.findIndex(
      (content: any) => content._id === currentContentData?._id
    );

    if (currentContentIndex === -1) return;

    if (currentContentIndex > 0) {
      // Move to the previous content in the same section
      setCurrentContentData(currentSection.contentId[currentContentIndex - 1]);
    } else {
      // Find the previous section
      let previousSectionIndex = section.findIndex(
        (section: any) => section._id === expandSectionId
      ) - 1;

      while (previousSectionIndex >= 0) {
        const previousSection = section[previousSectionIndex];
        if (previousSection && previousSection.contentId.length > 0) {
          // Move to the last content of the previous section
          setExpandSectionId(previousSection._id);
          setCurrentContentData(previousSection.contentId[previousSection.contentId.length - 1]);
          return;
        }
        // If the section has no content, continue to the previous section
        previousSectionIndex--;
      }

      // If no previous section has content, do nothing
      console.log("No more previous content available.");
    }
  };

  const countTotalCourseDuration = async () => {
    const totalTime = courseData.section?.reduce((contentAcc: number, section: any) => {
      return contentAcc + (section.contentId?.reduce((sum: number, content: any) => {
        if (!content?.duration.includes(":")) {
          return sum + Number(content?.duration);
        }
        return sum;
      }, 0) || 0);
    }, 0) || 0;

    const formateTime = convertMinutesToHours(String(totalTime))
    setTotalCourseDuration(formateTime)
  }

  useEffect(() => {
    if (currentContentData?.url?.includes('&index=')) {
      const url = modifyUrlFromIndex(currentContentData?.url, 'list');
      setDynamicUrl(url);
    } else {
      setDynamicUrl(currentContentData?.fileId ? `${process.env.REACT_APP_BASEURL}course/streamCourse/${currentContentData?.fileId}` : currentContentData?.url);
    }
  }, [currentContentData]);

  useEffect(() => {
    const handleScroll = () => {
      // You can adjust this threshold to determine when to minimize the player
      if (window.scrollY > 100) {
        // setIsMinimized(true);
      } else {
        setIsMinimized(false);
        const dragableDiv = document.getElementById('dragableDiv');

        if (dragableDiv) {
          dragableDiv.removeAttribute('style');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!expandSectionId && Object.keys(currentContentData).length === 0 && section.length) {
      setExpandSectionId(section[0]?._id)
      setCurrentContentData(section[0]?.contentId[0])
    } else {
      setExpandSectionId(expandSectionId)
      setCurrentContentData(currentContentData)
    }
    countTotalCourseDuration()
  }, [courseData])

  return (
    <Grid container spacing={1}>
      <style>
        {`
            .player-wrapper {
                position: relative;
                padding-top: 45%;
                /* Player ratio: 100 / (1280 / 720) */
            }
            .react-player {
                position: absolute;
                top: 0;
                left: 0;
            }
            img {
              width: 100%;
              height: 100%;
            }
            .MuiCardContent-root {
              padding: 0px !important;
            }
            .minimized-player {
              position: fixed;
              overflow:auto;
              resize:both;
              right: 10px;
              bottom: 60px;
              width: 300px;
              height: 200px;
              min-width: 300px;
              min-height: 200px;
              overflow-y: hidden;
              max-height:300px;
              max-width:400px;
              z-index: 9999;
            }
        `}
      </style>
      <Grid item xs={12}>
        <HeaderOption
          setSection={handleSideToggles}
          courseId={courseData?._id}
          courseName={courseData?.name}
          courseImage={courseData?.image}
          totalSections={courseData?.section?.length || 0}
          favorite={courseData?.favoriteCoursesByUser || []}
          showDictionaryIcon={false}
          showBookmarkIcon={false}
          showNoteIcon={false}
          disableIcons={currentContentData?.assessmentId ? true : false}
          navigationPath={'/my-learnings'}
          courseDuration={totalCourseDuration}
        />
      </Grid>
      {
        toggleSection &&
        <Grid item xs={12} sm={12} md={3} lg={3} sx={{ order: { xs: 2, sm: 2, md: 1 } }}>
          <MainCard
            title={courseData?.name}
            content={false}
            sx={{ position: 'sticky', p: 1, top: '80px', minHeight: 'calc(95vh - 80px)' }}
          >
            {section?.map((section: any, index: number) => (
              <Grid
                item
                xs={12}
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  mx: 1,
                  '& .MuiAccordion-root': {
                    border: 'none',
                    mt: '8px !important',
                    '& .MuiAccordionDetails-root': {
                      border: `1px solid ${theme.palette.divider} !important`,
                      borderRadius: '0 0 12px 12px'
                    },
                    '& .Mui-expanded': {
                      borderRadius: '12px 12px 0 0'
                    },
                    '.MuiTableCell-root': {
                      borderBottom: '0.3px solid silver !important'
                    }
                  }
                }}
              >
                <Accordion
                  expanded={expandSectionId === section?._id}
                  onChange={() => {
                    expandSectionId === section?._id ? handleChange(section?._id, '') : handleChange(section?._id);
                  }}
                >
                  <AccordionSummary
                    sx={{
                      flexDirection: 'row',
                      borderRadius: '12px',
                      backgroundColor: '#4680FF',
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        color: '#FFFFFF',
                      },
                    }}
                  >
                    <Typography variant="h5" color={"white"}>{section?.name}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: "10px" }}>
                    {section?.contentId?.map(
                      (item: any, sectionIndex: number) => (
                        <Box
                          key={sectionIndex}
                          sx={{
                            cursor: "pointer",
                            ...(currentContentData?._id === item._id && { backgroundColor: '#e9f0ff', borderRadius: '8px' }),

                          }}
                          onClick={() => setCurrentContentData(item)}

                        >
                          <Typography
                            sx={courseData?._id === item._id ? { backgroundColor: '#ebe8e8', borderRadius: '8px' } : {}}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
                              <Tooltip title={item?.type}>
                                <span>
                                  {(item?.type === 'video' ||
                                    item?.type === 'recorded_video' ||
                                    item?.type === 'whiteboardRecording' ||
                                    item?.type === 'vdoCipher' ||
                                    item?.type === 'vimeo') && <VideoCircle style={{ margin: '0px 8px -2px' }} size={18} />}
                                  {item?.type === 'audio' && <Music style={{ margin: '0px 8px -2px' }} size={18} />}
                                  {item?.type === 'presentation' && <PresentionChart style={{ margin: '0px 8px -2px' }} size={18} />}
                                  {item?.type === 'webContent' && <Cloud style={{ margin: '0px 8px -2px' }} size={18} />}
                                  {item?.type === 'content' && <Document style={{ margin: '0px 8px -2px' }} size={18} />}
                                  {item?.type === 'editor' && <Document style={{ margin: '0px 8px -2px' }} size={18} />}
                                  {item?.type === 'quiz' && <TaskSquare style={{ margin: '0px 8px -2px' }} size={18} />}
                                  {item.type === 'pdf' && <DocumentText1 variant="Bulk" style={{ margin: '0px 8px -2px' }} size={18} />}
                                  {item.type === 'doc' && <TableDocument variant="Bulk" style={{ margin: '0px 8px -2px' }} size={18} />}
                                  {item.type === 'ppt' && <DocumentText variant="Bulk" style={{ margin: '0px 8px -2px' }} size={18} />}
                                </span>
                              </Tooltip>
                              <Stack
                                direction="row"
                                alignItems="center"
                                sx={{
                                  width: '100%',
                                }}
                              >
                                <Typography
                                  fontWeight={500}
                                  sx={{
                                    flex: 1,
                                    fontSize: '14px',
                                    lineHeight: 1.5
                                  }}
                                >
                                  {item?.name}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '12px',
                                    lineHeight: 1.5,
                                    minWidth: '45px',
                                    textAlign: 'right'
                                  }}
                                >
                                  {convertMinutesToHours(item?.duration)}
                                </Typography>
                              </Stack>
                            </Box>
                          </Typography>
                        </Box>
                      )
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>
            ))}
          </MainCard>
        </Grid>
      }

      <Grid
        item
        xs={12}
        sm={12}
        md={toggleSection ? 9 : 12}
        lg={toggleSection ? 9 : 12}
        sx={{ order: { xs: 1, sm: 1, md: 2 }, padding: 0 }}
      >
        <Box sx={{ position: 'relative', boxShadow: 'none', flex: 1, marginBottom: '10px' }}>
          <MainCard>
            <Grid container sx={{ flexDirection: 'column' }}>
              <style>
                {`.video-container {
                    position: relative;
                  }

                  .watermark {
                    position: absolute;
                    top: 10px;
                    left: 10px;
                    background-color: rgba(0, 0, 0, 0.5);
                    color: white;
                    padding: 5px;
                    border-radius: 5px;
                  }`
                }
              </style>
              
              <Grid item sx={{ width: 'inherit', px: 3, mt: 2 }}>
                {(currentContentData?.url || currentContentData?.fileId || currentContentData?.vdoCipherId || currentContentData?.vimeoId) && (
                  <div
                    draggable={isMinimized}
                    id={isMinimized ? 'dragableDiv' : ''}
                    className={
                      vdoCipherPreview || vimeoPreview
                        ? isMinimized
                          ? 'minimized-player '
                          : ''
                        : isMinimized
                          ? 'minimized-player '
                          : 'player-wrapper'
                    }
                    // className='player-wrapper'
                    style={{
                      transform: `translate(${position.x}px, ${position.y}px)`,
                      cursor: isMinimized && dragging ? 'grabbing' : 'pointer'
                    }}
                  // onMouseDown={handleMouseDown}
                  >
                    <ReactPlayer
                      ref={videoPlayer}
                      url={dynamicUrl}
                      className="react-player"
                      playing={isPlaying}
                      controls // Disable controls
                      width="100%"
                      height="100%"
                      config={{
                        youtube: {
                          playerVars: {
                            showinfo: 0,
                            rel: 0,
                            controls: currentContentData?.skipAble ? 'nodownload' : 0
                            // controls: 0
                          }
                        },
                        file: {
                          attributes: {
                            controlsList: 'nodownload' // Disable download for file-based videos
                            // crossOrigin: localStorage.getItem('serviceToken') || null
                          }
                        }
                      }}
                      style={vdoCipherPreview || vimeoPreview ? { display: 'none' } : {}}
                    />
                    {/* )} */}
                    <Box
                      sx={vdoCipherPreview || vimeoPreview ? { display: 'none' } : isMinimized ? { ml: 0, mt: 1 } : {}}
                      className="watermark"
                    >
                      <Avatar
                        src={
                          setting?.path
                            ? process.env.REACT_APP_FILE_URL + setting?.path
                            : `data:${setting?.mimetype};base64,${setting?.file}`
                        }
                      />
                    </Box>
                    <Box
                      ref={vdoCipherRef}
                      sx={
                        vdoCipherPreview
                          ? {
                            width: 'auto',
                            background: 'red',
                            height: isMinimized ? '200px' : { xs: '54vh', md: '60vh', lg: '70vh' },
                            borderRadius: '8px',
                            backgroundColor: 'white',
                            position: 'relative',
                            boxShadow: '0 2px 20px 7px rgb(0 0 0 / 5%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }
                          : { display: 'none' }
                      }
                    />
                    <Box
                      ref={vimeoRef}
                      sx={
                        vimeoPreview
                          ? {
                            width: 'auto',
                            background: 'red',
                            height: isMinimized ? '200px' : { xs: '54vh', md: '60vh', lg: '70vh' },
                            borderRadius: '8px',
                            backgroundColor: 'white',
                            position: 'relative',
                            boxShadow: '0 2px 20px 7px rgb(0 0 0 / 5%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }
                          : { display: 'none' }
                      }
                    />
                  </div>
                )}
              </Grid>
              <DocumentViewer currentContentData={currentContentData} disableFlag={false} />
              {/* <Grid item sx={{ width: 'inherit', px: 3 }}>
                {currentContentData?.file &&
                  (currentContentData?.file?.mimetype ===
                    'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
                    currentContentData?.file?.mimetype === 'application/vnd.ms-powerpoint' ? (
                    <iframe
                      src={`https://view.officeapps.live.com/op/embed.aspx?src=${process.env.REACT_APP_FILE_URL + currentContentData?.file?.path
                        }`}
                      width="100%"
                      height="450px"
                      title="Embedded Office Presentation"
                    />
                  ) : currentContentData?.file?.mimetype === 'application/msword' ? (
                    <div style={{ textAlign: 'center', width: '100%' }}>
                      <Box
                        sx={{
                          p: 1,
                          py: 1.5,
                          maxWidth: '750px',
                          maxHeight: 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          margin: 'auto'
                        }}
                      >
                      </Box>
                    </div>
                  ) : (
                    <Box
                      className="textDiv"
                      sx={{
                        color: 'black',
                        height: '100vh',
                        width: '100%',
                        overflow: 'auto',
                      }}
                    >
                      <object
                        data={
                          currentContentData?.file?.path
                            ? process.env.REACT_APP_FILE_URL + currentContentData?.file?.path
                            : `data:${currentContentData?.file?.mimetype};base64,${currentContentData?.file?.file}`
                        }
                        type={currentContentData?.file?.mimetype}
                        width="100%"
                        height="100%"
                      />
                    </Box>
                  ))}
              </Grid> */}
              <Grid item sx={{ width: 'inherit', position: 'relative', px: 3 }}>
                <Box
                  className="textDiv"
                  dangerouslySetInnerHTML={{ __html: currentContentData?.details }}
                />
              </Grid>
            </Grid>
            <Box
              sx={{
                position: 'relative',
                boxShadow: 'none',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-evenly'
              }}
            >
              <Grid container alignItems="center" justifyContent="space-evenly" sx={{ mt: 2 }}>
                {/* Completed/Complete and Continue Button */}
                <Grid item sx={{ mb: 1 }}>
                  <AnimateButton>
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={() => goToNextContent()}
                    >
                      Next
                    </Button>
                  </AnimateButton>
                </Grid>
              </Grid>
            </Box>
          </MainCard>
        </Box>
      </Grid>
    </Grid>
  );
};

export default SuperAdminCourseView